import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { committeeActionCreators } from '../../../stores/lis-committee-store';
import { memberActionCreators } from '../../../stores/lis-members-store';
import 'tinymce/themes/silver/theme';
import 'tinymce/icons/default'
import 'tinymce/plugins/paste/plugin';
import 'tinymce/plugins/link/plugin';
import 'tinymce/plugins/save/plugin';
import 'tinymce/plugins/lists/plugin';
import 'tinymce/plugins/code/plugin';
import 'tinymce/plugins/preview/plugin';
import 'tinymce/plugins/searchreplace/plugin';
import 'tinymce/plugins/pagebreak/plugin';
import 'tinymce/plugins/table/plugin';
import '../../../stylesheets/tinymce/oxide/skin.min.css';
import '../../../stylesheets/tinymce/legislation_text_stylesheet.scss';
import '../../../stylesheets/tinymce/custom.scss';
import { Editor } from '@tinymce/tinymce-react';
import '../../../lis-shared/tinymce/rejectedButtonPlugin';
import '../../../lis-shared/tinymce/agreedButtonPlugin';
import '../../../lis-shared/tinymce/createAmendmentPlugin';
import '../../../lis-shared/tinymce/bracketButtonsPlugin';
import tinymce from 'tinymce';
import SpellCheck from './lis-spellcheck';
import { alternativeNormalize, getNextSibling, insertFromClipboard, closestParentOfType, removeSpellingErrors } from '../../../services/editor-manipulation';
import ReactToPrint from 'react-to-print';

const WebEditor = (props) => {
    const [members, setMembers] = useState([]);
    const patronsDisplayNames = useMemo(() => {
        if (props.members && props.members.memberList) {
            return props.members.memberList.map(m => m.PatronDisplayName)
        } else {
            return [];
        }
    }, [props.members.memberList]);

    const [committees, setCommittees] = useState([]);
    const [selectedBillNumberIsLoaded, toggleSelectedBillNumberIsLoaded] = useState(true);
    const [selectedSponsorIsLoaded, toggleSelectedSponsorIsLoaded] = useState(true);
    const [selectedMemberIsLoaded, toggleSelectedMemberIsLoaded] = useState(true);
    const [selectedCommitteeIsLoaded, toggleSelectedCommitteeIsLoaded] = useState(true);

    const [memberIsLoaded, toggleMemberIsLoaded] = useState(false);
    const [committeeIsLoaded, togggleCommitteeIsLoaded] = useState(false);

    const [subEditors, setSubEditors] = useState([]);

    const prevProps = usePreviousValue(props);

    //const [content, setContent] = useState(props.editorContent.replaceAll(/<\/a>,/g, '</a>\u200B,'));

    const [lines, setLines] = React.useState([]);

    const [initializedCount, incrementInitializedCount] = useState(0);
    const [pauseForClasses, setPauseForClasses] = useState(-1);

    const [isLoaded, toggleLoaded] = useState(false);
    const [isCleaned, toggleCleaned] = useState(false);

    const [clearEditors, toggleClearEditors] = useState(false);

    const [selectedP, setSelectedP] = useState(null);

    const resizeObserverRef = useRef();

    let printRef = useRef();

    useEffect(() => {
        if (props.sessionId) {
            loadMembers(props.sessionId);
            loadCommittees(props.sessionId);
        } else {
            togggleCommitteeIsLoaded(true);
            toggleMemberIsLoaded(true);
        }

        initializeEditors(props.editorContent);

        return () => {
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
            }
        };
    }, []);

    useEffect(() => {
        if (!memberIsLoaded && props.members && props.members?.memberList.length > 0) {
            const members = props.members.memberList
                .filter(m => m.ListDisplayName)
                .map(m => ({ value: (m.ChamberCode === "H" ? "DEL. " : "SEN. ") + m.PatronDisplayName, text: m.ListDisplayName }));
            setMembers(members);
            toggleMemberIsLoaded(true);
        }
    }, [props.members.memberList, memberIsLoaded]);

    useEffect(() => {
        if (!committeeIsLoaded && props.committee && props.committee?.committeeList.length > 0) {
            const committees = props.committee.committeeList.filter(x => !x.ParentCommitteeID).map(c => ({ value: c.Name, text: c.Name }));
            setCommittees(committees);
            togggleCommitteeIsLoaded(true);
        }
    }, [props.committee.committeeList, committeeIsLoaded]);

    useEffect(() => {
        // API isn't doing the header, so don't touch it here either. It's always in a div right?
        let editorContent = props.editorContent;
        if (editorContent) {
            const endOfHeaderIndex = editorContent.indexOf('</div>');
            if (endOfHeaderIndex > -1) {
                editorContent = editorContent.substring(0, endOfHeaderIndex) + editorContent.substring(endOfHeaderIndex).replaceAll('§ <a', '§\u00a0<a')
            } else {
                editorContent = editorContent.replaceAll('§ <a', '§\u00a0<a');
            }
        }
        setSubEditors([editorContent]);
    }, [props.reviewKey])

    useEffect(() => {
        if (prevProps?.sessionId && props.sessionId.sessionId !== prevProps?.sessionId.sessionId) {
            loadMembers(props.sessionId);
            loadCommittees(props.sessionId);
        }
    }, [props.sessionId]);

    useEffect(() => {
        //below is annoying and seems useless but is needed in order to get the add amendment wizard to auto-populate based on user's sponsor/member/cmte choices
        if ((prevProps?.billNumber !== undefined && prevProps?.billNumber !== props.billNumber) ||
            (prevProps?.selectedSponsor !== undefined && prevProps?.selectedSponsor !== props.selectedSponsor) ||
            (prevProps?.selectedMember !== undefined && prevProps?.selectedMember !== props.selectedMember) ||
            (prevProps?.selectedCommittee !== undefined && prevProps?.selectedCommittee !== props.selectedCommittee)) {
            toggleSelectedBillNumberIsLoaded(false);
            toggleSelectedSponsorIsLoaded(false);
            toggleSelectedMemberIsLoaded(false);
            toggleSelectedCommitteeIsLoaded(false);
        }
    }, [props.billNumber, props.selectedSponsor, props.selectedMember, props.selectedCommittee]);

    useEffect(() => {
        toggleSelectedBillNumberIsLoaded(true);
        toggleSelectedSponsorIsLoaded(true);
        toggleSelectedMemberIsLoaded(true);
        toggleSelectedCommitteeIsLoaded(true);
    }, [selectedBillNumberIsLoaded, selectedSponsorIsLoaded, selectedMemberIsLoaded, selectedCommitteeIsLoaded]);

    useEffect(() => {
        if (members && members.length > 0) {
            if (props.membersCallback) {
                props.membersCallback(props.members.memberList);
                toggleMemberIsLoaded(true);
            } else {
                toggleMemberIsLoaded(true);
            }
        }
    }, [members]);

    useEffect(() => {
        if (committees && committees.length > 0) {
            if (props.committeesCallback) {
                props.committeesCallback(props.committee.committeeList);
                togggleCommitteeIsLoaded(true);
            } else {
                togggleCommitteeIsLoaded(true);
            }
        }
    }, [committees]);

    useEffect(() => {
        if (pauseForClasses > -1) {
        }
    }, [pauseForClasses]);

    useEffect(() => {
        if (initializedCount > 0 && initializedCount === subEditors.length) {
            toggleLoaded(true);
        }
    }, [initializedCount]);

    // Yeah, its stupid, but otherwise we cant get to the innerhtml
    // Have to add catchln too ...
    useEffect(() => {
        if (isLoaded && !isCleaned) {
            const subEditors = [];
            const editors = document.getElementsByClassName('mce-content-body');
            Array.from(editors).forEach((editorDiv, index) => {
                const ps = editorDiv.getElementsByTagName('p');
                if (index > 0) {
                    const noClass = Array.from(ps).filter(p => !p.className);
                    noClass.forEach(p => {
                        if (p.textContent.charAt(0) === '§') {
                            p.className = 'catchln';
                        } else {
                            p.className = 'section';
                        }
                    });
                }
                const spans = editorDiv.getElementsByTagName('span');
                Array.from(spans).filter(s => s.className !== 'bracket').forEach(s => {
                    Array.from(s.childNodes).forEach(sc => s.parentElement.insertBefore(sc, s));
                    s.parentElement.removeChild(s);
                })
                Array.from(ps).forEach(p => {
                    alternativeNormalize(p);
                })
                subEditors.push(editorDiv.innerHTML);
            })
            setSubEditors(subEditors);
            toggleCleaned(true);
        }
    }, [isLoaded]);

    useEffect(() => {
        if (isCleaned) {
            tinymce.editors.forEach((ed, i) => {
                updateEditor(ed.getContent(), i);
                ed.undoManager.clear()
                props.handleEditorChange(false);
            });
        }
    }, [isCleaned]);

    useEffect(() => {
        if (props.hasSaved) {
            toggleClearEditors(true);
            setSubEditors([]);
        }
    }, [props.hasSaved]);

    useEffect(() => {
        if (clearEditors) {
            initializeEditors(props.editorContent);
            toggleClearEditors(false);
            props.toggleHasSaved(false);
        }
    }, [clearEditors]);

    const initializeEditors = (incomingContent) => {
        // Poor man's html normalizing
        const splitContent = incomingContent.split('<p class="catchln">').map(c => c.replaceAll('</em><em class="new">', '')
            .replaceAll('</em><em>', '').replaceAll('<em><em class="new">', '<em class="new">').replaceAll('<em><em>', '<em>').replaceAll('</s><s>', '')
            .replaceAll('\n', '').replaceAll('<p class="section"> </p>', ''))
        // Performance feels really bad, but I can't find a happy medium
        // Only use one of these at a time
        // Ideally we go with one editor per section
        const oneEditorPerSection = (sContent) => sContent.map((content, index) =>
            index === 0 ? content : `<p class="catchln">${content}`)

        // Keep the header separate
        const oneOkayTwoEditorsTotal = (sContent) => {
            let firstEditor = sContent.shift();
            if (sContent.length === 0) {
                return [firstEditor];
            } else {
                return [firstEditor, sContent.map(s => `<p class="catchln">${s}`).join('')];
            }
        }

        // One editor for the whole thing
        // Would theoretically be equivalent to the old editor, but I did add some more logic, so it isn't
        const oneEditorTotal = (sContent) => {
            let firstEditor = sContent.shift();
            if (sContent.length === 0) {
                return [firstEditor];
            } else {
                return [firstEditor + sContent.map(s => `<p class="catchln">${s}`).join('')];
            }
        }

        const upToTwentyEditors = (sContent) => {
            const chunkSize = Math.ceil((sContent.length) / 20);
            const newContent = [];
            for (let i = 0; i < 20; i++) {
                newContent.push(sContent.slice(i * chunkSize, (i + 1) * chunkSize).join(''));
            }
            return newContent.filter(c => c !== '');
        }
        let newContent = oneEditorTotal(splitContent);
        setSubEditors(newContent);
    }

    const loadMembers = (sessionId) => {
        toggleMemberIsLoaded(false);
        props.actions.getMemberList(`sessionID=${sessionId}`)
            .then(() => {
            }).catch(() => {
                toggleMemberIsLoaded(true);
            });
    }

    const loadCommittees = (sessionId) => {
        togggleCommitteeIsLoaded(false);
        props.actions.getCommitteeList(`sessionID=${sessionId}` + "&includeSubCommittees=true")
            .then(() => {
            }).catch(() => {
                togggleCommitteeIsLoaded(true);
            });
    }

    const updateEditor = (content, index) => {
        // Content that gets saved shouldn't have invisible spaces. That could go over poorly in iText.
        if (isCleaned) {
            const updatedSubEditors = [...subEditors];
            updatedSubEditors[index] = content;
            setSubEditors(updatedSubEditors);
            // Need to get the content out of the editors some other way ... this is too slow
            //setContent(updatedSubEditors.join('').replaceAll(zeroWidthSpace, ''));
            //props.handleEditorChange(updatedSubEditors.join('').replaceAll(zeroWidthSpace, ''));
            const dirty = tinymce.editors.filter(ed => ed.undoManager.hasUndo());
            props.handleEditorChange(dirty.length > 0)
            props.toggleAllowGoToMisspelling(false);
        }
    }

    const calculateLineNumbers = useCallback(node => {
        if (resizeObserverRef.current) {
            resizeObserverRef.current.disconnect();
        }
        if (node !== null) {
            resizeObserverRef.current = new ResizeObserver(() => {
                if (['Engrossed', 'Reengrossed', 'Introduced'].includes(props.currentLegType)) {
                    const divs = document.getElementsByClassName('mce-content-body');
                    const children = Array.from(divs).flatMap(d => Array.from(d.children));
                    const newLines = [];
                    let currentLine = 1;
                    let trackLines = false;
                    for (let i = 0; i < children.length; i++) {
                        const child = children[i];
                        if (child.nodeName === 'DIV') {
                            const newPs = [];
                            for (let j = 0; j < child.children.length; j++) {
                                const child2 = child.children[j];
                                if (child2.className === 'billnumber') {
                                    trackLines = true;
                                }
                                if (child2.className === 'billpatron') {
                                    trackLines = false;
                                }
                                // Handle links in the ld title
                                const lineHeight = child2.className === 'ldtitle' ? 32 : 24;
                                const lineCount = Math.floor(child2.clientHeight / lineHeight);
                                let textContent = ''
                                for (let i = currentLine; i < currentLine + lineCount; i++) {
                                    textContent += (trackLines ? i : '\u00a0') + '\n';
                                }
                                if (trackLines) {
                                    currentLine += lineCount;
                                }
                                if (child2.className === 'billpatron') {
                                    trackLines = true;
                                }
                                newPs.push(<p className={child2.className}>{textContent}</p>);
                            };
                            newLines.push(<div>
                                {newPs}
                            </div>);
                        } else if (child.nodeName === 'P') {
                            if (child.className === 'billpatron') {
                                continue;
                            }
                            const lineCount = Math.floor(child.clientHeight / 32);
                            let textContent = '';
                            for (let i = currentLine; i < currentLine + lineCount; i++) {
                                textContent += (i + '\n');
                            }
                            currentLine += lineCount;
                            newLines.push(<p className={child.className}>{textContent}</p>);
                        } else if (child.nodeName === 'TABLE') {
                            let tableElements = [];
                            Array.from(child.children).forEach((child2) => {
                                const trs = [];
                                Array.from(child2.children).forEach((tr) => {
                                    const pClass = Array.from(tr.children).find(cch => cch.firstChild?.nodeName === 'P').firstChild.className ?? '';
                                    const lineCount = Math.floor(tr.clientHeight / 32);
                                    let textContent = "" + currentLine;
                                    if (lineCount > 1) {
                                        for (let i = currentLine + 1; i < currentLine + lineCount; i++) {
                                            textContent += '\n' + i
                                        }
                                        textContent = textContent.trim();
                                    }
                                    trs.push(<tr><td><p className={pClass}>{textContent}</p></td></tr>)
                                    currentLine += lineCount;
                                })
                                if (child2.nodeName === 'THEAD') {
                                    tableElements.push(<thead>{trs}</thead>)
                                } else if (child2.nodeName === 'TBODY') {
                                    tableElements.push(<tbody>{trs}</tbody>)
                                }
                            })
                            newLines.push(<table>
                                {tableElements}
                            </table>);
                        };
                    };
                    setLines(newLines);
                } else if (['Enrolled', 'Reenrolled'].includes(props.currentLegType)) {
                    const divs = document.getElementsByClassName('mce-content-body');
                    const children = Array.from(divs).flatMap(d => Array.from(d.children));
                    const newLines = [];
                    let currentLine = 1;
                    let trackLines = false;
                    for (let i = 0; i < children.length; i++) {
                        const child = children[i];
                        if (child.nodeName === 'DIV') {
                            const newPs = [];
                            for (let j = 0; j < child.children.length; j++) {
                                const child2 = child.children[j];
                                // Handle links in the ld title
                                const lineHeight = child2.className === 'ldtitle' ? 32 : 24;
                                const lineCount = Math.floor(child2.clientHeight / lineHeight);
                                let textContent = ''
                                for (let i = currentLine; i < currentLine + lineCount; i++) {
                                    textContent += (trackLines ? i : '\u00a0') + '\n';
                                }
                                if (trackLines) {
                                    currentLine += lineCount;
                                }
                                if (child2.textContent.includes('ENROLLED') || child2.textContent.includes('REENROLLED')) {
                                    trackLines = true;
                                }
                                newPs.push(<p className={child2.className}>{textContent}</p>);
                            };
                            newLines.push(<div>
                                {newPs}
                            </div>);
                        } else if (child.nodeName === 'P') {
                            if (child.className === 'billpatron') {
                                continue;
                            }
                            const lineCount = Math.floor(child.clientHeight / 32);
                            let textContent = '';
                            for (let i = currentLine; i < currentLine + lineCount; i++) {
                                textContent += (i + '\n');
                            }
                            currentLine += lineCount;
                            newLines.push(<p className={child.className}>{textContent}</p>);
                        } else if (child.nodeName === 'TABLE') {
                            let tableElements = [];
                            Array.from(child.children).forEach((child2) => {
                                const trs = [];
                                Array.from(child2.children).forEach((tr) => {
                                    const pClass = Array.from(tr.children).find(cch => cch.firstChild?.nodeName === 'P').firstChild.className ?? '';
                                    const lineCount = Math.floor(tr.clientHeight / 32);
                                    let textContent = "" + currentLine;
                                    if (lineCount > 1) {
                                        for (let i = currentLine + 1; i < currentLine + lineCount; i++) {
                                            textContent += '\n' + i
                                        }
                                        textContent = textContent.trim();
                                    }
                                    trs.push(<tr><td><p className={pClass}>{textContent}</p></td></tr>)
                                    currentLine += lineCount;
                                })
                                if (child2.nodeName === 'THEAD') {
                                    tableElements.push(<thead>{trs}</thead>)
                                } else if (child2.nodeName === 'TBODY') {
                                    tableElements.push(<tbody>{trs}</tbody>)
                                }
                            })
                            newLines.push(<table>
                                {tableElements}
                            </table>);
                        };
                    };
                    setLines(newLines);
                } else if (props.currentLegType === 'Chaptered') {
                    setLines([]);
                } else if (props.currentLegType === 'Substitute') {
                    // pop off the first line?
                    // There is no header ?
                    const divs = document.getElementsByClassName('mce-content-body');
                    const children = Array.from(divs).flatMap(d => Array.from(d.children));
                    const newLines = [];
                    let currentLine = 1;
                    let trackLines = false;
                    for (let i = 0; i < children.length; i++) {
                        const child = children[i];
                        if (child.nodeName === 'DIV') {
                            const newPs = [];
                            for (let j = 0; j < child.children.length; j++) {
                                const child2 = child.children[j];
                                // Handle links in the ld title
                                const lineHeight = child2.className === 'ldtitle' ? 32 : 24;
                                const lineCount = Math.floor(child2.clientHeight / lineHeight);
                                let textContent = ''
                                for (let i = currentLine; i < currentLine + lineCount; i++) {
                                    textContent += (trackLines ? i : '\u00a0') + '\n';
                                }
                                if (trackLines) {
                                    currentLine += lineCount;
                                }
                                if (child2.textContent.trim() !== '') {
                                    trackLines = true;
                                }
                                newPs.push(<p className={child2.className}>{textContent}</p>);
                            };
                            newLines.push(<div>
                                {newPs}
                            </div>);
                        } else if (child.nodeName === 'P') {
                            if (child.textContent.trim() === '' && i === 0) {
                                newLines.push(<p className={child.className}> </p>);
                                continue;
                            }
                            const lineCount = Math.floor(child.clientHeight / 32);
                            let textContent = '';
                            for (let i = currentLine; i < currentLine + lineCount; i++) {
                                textContent += (i + '\n');
                            }
                            currentLine += lineCount;
                            newLines.push(<p className={child.className}>{textContent}</p>);
                        } else if (child.nodeName === 'TABLE') {
                            let tableElements = [];
                            Array.from(child.children).forEach((child2) => {
                                const trs = [];
                                Array.from(child2.children).forEach((tr) => {
                                    const pClass = Array.from(tr.children).find(cch => cch.firstChild?.nodeName === 'P').firstChild.className ?? '';
                                    const lineCount = Math.floor(tr.clientHeight / 32);
                                    let textContent = "" + currentLine;
                                    if (lineCount > 1) {
                                        for (let i = currentLine + 1; i < currentLine + lineCount; i++) {
                                            textContent += '\n' + i
                                        }
                                        textContent = textContent.trim();
                                    }
                                    trs.push(<tr><td><p className={pClass}>{textContent}</p></td></tr>)
                                    currentLine += lineCount;
                                })
                                if (child2.nodeName === 'THEAD') {
                                    tableElements.push(<thead>{trs}</thead>)
                                } else if (child2.nodeName === 'TBODY') {
                                    tableElements.push(<tbody>{trs}</tbody>)
                                }
                            })
                            newLines.push(<table>
                                {tableElements}
                            </table>);
                        };
                    };
                    setLines(newLines);
                }
            })
            resizeObserverRef.current.observe(node);
        }
    }, [props.currentLegType])

    return <>
        {(!memberIsLoaded || !committeeIsLoaded || !selectedBillNumberIsLoaded || !selectedSponsorIsLoaded || !selectedMemberIsLoaded || !selectedCommitteeIsLoaded) ?
            <div className="spinner center-spinner" />
            :
            <React.Fragment key={props.reviewKey}>
                <div className='inner-grid eight-and-one'>
                    <div />
                    <div style={{ display: "inline-flex", marginBottom: "20px", marginTop: props.location === "/amendment-management" ? "13px" : "8px" }}>
                    </div>
                </div>
                <div id="interface-container" ref={el => (printRef = el)} style={{ display: 'grid', gridTemplateColumns: '1px 1fr 1fr', gap: '10px' }}>
                    <div id='other-line-numbers' >
                        {lines}
                    </div>
                    <div ref={calculateLineNumbers} id="editor-container">
                        {subEditors.map((c, index) =>
                            <Editor
                                key={index}
                                value={c}
                                init={{
                                    setup: (editor) => {
                                        editor.ui.registry.addToggleButton('sectionButton', {
                                            text: "§",
                                            tooltip: "Insert Section Symbol",
                                            onAction: function () {
                                                editor.insertContent('§');
                                            }
                                        });
                                        editor.ui.registry.addButton('spacesButton', {
                                            text: '¶',
                                            tooltip: 'Show spaces',
                                            onAction: () => {
                                                const ed = document.getElementById('editor-container');
                                                const button = document.querySelector('[title="Show spaces"]');
                                                if (ed.classList.contains('show-spaces')) {
                                                    ed.classList.remove('show-spaces');
                                                    button?.classList.remove('tox-tbtn--enabled');
                                                } else {
                                                    ed?.classList.add('show-spaces');
                                                    button?.classList.add('tox-tbtn--enabled');
                                                }
                                                editor.focus();
                                            }
                                        });
                                        editor.on('keyup', e => {
                                            const rng = editor.selection.getRng();
                                            if (e.key === 'Enter') {
                                                const { startContainer, collapsed } = rng;
                                                if (startContainer.nodeName === 'SPAN' && startContainer.className.includes('bracket') && collapsed) {
                                                    startContainer.parentElement.insertBefore(startContainer.childNodes[0], startContainer);
                                                    startContainer.remove()
                                                }
                                            } else {
                                                const { startContainer, startOffset } = rng;
                                                const closestP = closestParentOfType(startContainer, 'P');
                                                // Don't touch the header, which is in a DIV that has no class
                                                if (closestP.parentElement?.className?.includes('mce-content-body') && startContainer.nodeName === '#text' && startContainer.textContent.endsWith('§ ') && startContainer.nextSibling?.nodeName === 'A') {
                                                    // Replacing the text like this forces the cursor back to the start of the text node. So set it back ... again
                                                    const text = startContainer.textContent;
                                                    startContainer.textContent = text.substring(0, text.length - 2) + '§\u00a0';
                                                    document.getSelection().removeAllRanges();
                                                    const range = document.createRange();
                                                    range.setStart(startContainer, startOffset);
                                                    range.setEnd(startContainer, startOffset);
                                                    document.getSelection().addRange(range);
                                                }
                                            }
                                        })
                                        editor.on("keydown", async (e) => {
                                            const rng = editor.selection.getRng();
                                            const { startContainer, startOffset } = rng;
                                            if (e.ctrlKey && e.key.toLowerCase() === 'v') {
                                                e.preventDefault();
                                                if (e.shiftKey) {
                                                    await insertFromClipboard(true, editor);
                                                } else {
                                                    await insertFromClipboard(false, editor);
                                                }
                                            }
                                            else if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Enter'].includes(e.key)) {
                                                // do nothing
                                            }
                                            else if (rng.collapsed && e.key.length === 1) {
                                                if (startContainer.nodeName === '#text' && startContainer.parentElement.nodeName === 'SPAN' && startContainer.parentElement.className.includes('bracket')) {
                                                    const nextSibling = getNextSibling(startContainer.parentElement);
                                                    if (nextSibling) {
                                                        nextSibling.textContent = e.key + nextSibling.textContent;
                                                        e.preventDefault();
                                                        setTimeout(() => { // When in doubt, time0ut
                                                            const newRange = document.createRange();
                                                            newRange.setStart(nextSibling, 1);
                                                            newRange.setEnd(nextSibling, 1);
                                                            document.getSelection().removeAllRanges();
                                                            document.getSelection().addRange(newRange);
                                                            editor.undoManager.add();
                                                        }, 0);
                                                    } else {
                                                        e.preventDefault();
                                                    }
                                                }
                                                else if (startContainer.nodeName === 'SPAN' && startContainer.className.includes('bracket')) {
                                                    const newRange = document.createRange();
                                                    newRange.setStartBefore(startContainer);
                                                    newRange.setEndBefore(startContainer);
                                                    document.getSelection().removeAllRanges();
                                                    document.getSelection().addRange(newRange);
                                                    // Otherwise, this doesn't add a space at the end of a paragraph and moves the cursor to the start of it.
                                                    // It would add the whitespace and then immediately collapse, so it would be invisible and the range no longer fit.
                                                } else if (e.key === ' ' && startContainer.nodeName === '#text' && startContainer.parentElement.nodeName === 'P' &&
                                                    startContainer.parentElement.lastChild === startContainer && startOffset === startContainer.length) {
                                                    if (!editor.undoManager.hasUndo() && !editor.undoManager.hasRedo()) {
                                                        editor.undoManager.add();
                                                    }
                                                    e.preventDefault();
                                                    startContainer.textContent += '\u00a0';
                                                    const newRange = document.createRange();
                                                    newRange.setStart(startContainer, startOffset + 1);
                                                    newRange.setEnd(startContainer, startOffset + 1);
                                                    document.getSelection().removeAllRanges();
                                                    document.getSelection().addRange(newRange);
                                                    editor.undoManager.add();
                                                } else {

                                                }
                                            }
                                        })
                                        editor.on("click", (e) => {
                                        })
                                        editor.on("paste", async (e) => {
                                            const types = e.clipboardData.types;
                                            if (types.includes('text/html')) {
                                                e.preventDefault();
                                                await insertFromClipboard(false, editor);
                                            } else if (types.includes('text/plain')) {
                                                e.preventDefault();
                                                await insertFromClipboard(true, editor);
                                            } else {
                                                /// ???
                                            }
                                        })
                                        // Stop TinyMCE from adding an em in a span
                                        // Guess we would also have to split an em if its already italics?
                                        // Actually, looks like its fine when you move the cursor out of the span/em
                                        editor.on('BeforeExecCommand', (e) => {
                                            // if (e.command === 'mceToggleFormat' && e.value === 'italic') {
                                            //     const elements = document.querySelectorAll('[aria-label="Italic"]');
                                            //     if (elements.length > 0 && !elements[0].classList.contains('tox-tbtn--enabled')) {
                                            //         const rng = editor.selection.getRng();
                                            //         const currentNode = rng.startContainer;
                                            //         if (rng.collapsed) {
                                            //             e.preventDefault();
                                            //             editor.insertContent(`<em class="new">${zeroWidthSpace}</em>`);
                                            //             if (currentNode.nextSibling?.nodeName === 'EM') {
                                            //                 const newRange = document.createRange();
                                            //                 newRange.setStart(currentNode.nextSibling, 0);
                                            //                 newRange.setEnd(currentNode.nextSibling, 0);
                                            //                 document.getSelection().removeAllRanges();
                                            //                 document.getSelection().addRange(newRange);
                                            //             }
                                            //         }
                                            //     }
                                            // }
                                            // Otherwise it works ```fine```
                                        })
                                        editor.on('selectionchange', (e, b) => {
                                            const range = editor.selection.getRng();
                                            const currentP = closestParentOfType(range.startContainer, 'P');
                                            setSelectedP(currentP);
                                            if (editor.hasFocus()) {
                                                const range = editor.selection.getRng();
                                                const currentP = closestParentOfType(range.startContainer, 'P');
                                                if (currentP) {
                                                    removeSpellingErrors(currentP, props.toggleAllowGoToMisspelling(false));
                                                }
                                            }
                                        })
                                    },
                                    browser_spellcheck: false,
                                    menubar: 'format tools table',
                                    plugins: 'save lists preview searchreplace pagebreak code table bracketbuttons ' + (props.allowAmendmentActions ? ' rejectedbutton agreedbutton createamendment' : ''),
                                    toolbar: ['styleselect | fontsizeselect', 'bold italic strikethrough underline | alignleft aligncenter alignright', 'lBracketbutton rBracketbutton bracketButton pasteWithBrackets | sectionButton spacesButton | undo redo', (props.allowAmendmentActions && !props.overrideRemoveAgreeRejectButtons ? ' | rejectedbuttons agreedbutton' : '') + (props.allowAmendmentActions && !props.overrideRemoveCreateAmendmentButton ? ' | createamendment' : '')],
                                    contextmenu: props.allowAmendmentActions ? 'rejectedbuttonscontext agreedbuttoncontext' : '',
                                    skin: false,
                                    members: members,
                                    committees: committees,
                                    valid_children: '+body[style]',
                                    content_css: false,
                                    content_style: 'table {border-collapse: collapse; border: 1px solid #FFFFFF; }' + 'td {padding: 12px 0px; }',
                                    userChamber: props.userChamber,
                                    billNumber: props.billNumber,
                                    sponsor: props.selectedSponsor ? props.selectedSponsor.value : '',
                                    member: props.selectedMember && members.find(m => m.text === props.selectedMember.value.ListDisplayName) ? members.find(m => m.text === props.selectedMember.value.ListDisplayName).value : '',
                                    committee: props.selectedCommittee && committees.find(c => c.text === props.selectedCommittee.value.Name) ? committees.find(c => c.text === props.selectedCommittee.value.Name).value : '',
                                    amendmentOrigin: props.selectedMember && members.find(m => m.text === props.selectedMember.value.ListDisplayName) ? props.selectedMember.value.ChamberCode : props.selectedCommittee && committees.find(c => c.text === props.selectedCommittee.value.Name) ? props.selectedCommittee.value.ChamberCode : props.amendmentOrigin ? props.amendmentOrigin : '',
                                    allowGovernorAmendmentSponsor: props.allowGovernorAmendmentSponsor,
                                    paste_data_images: true,
                                    table_default_attributes: {
                                        border: '0'
                                    },
                                    formats: formats,
                                    style_formats: style_formats,
                                    inline: true,
                                    init_instance_callback: (ed) => incrementInitializedCount(n => n + 1),
                                    fixed_toolbar_container: '#render-toolbar-here',
                                }}
                                onEditorChange={(e) => updateEditor(e, index)}
                            />)}
                    </div>
                    <div style={{ borderLeft: '1px solid black' }}>
                        <div id='render-toolbar-here' style={{ height: '200px', position: 'sticky', top: '10px' }} />
                        <ReactToPrint
                            trigger={() => <button type="button" className="button primary" style={{ marginBottom: "5px", position: 'sticky', top: '180px', zIndex: 2 }}> Print</button>}
                            content={() => printRef}
                            pageStyle={`
                                @media print {
                                    p {
                                        page-break-inside: avoid;
                                    }

                                    #interface-container > * {
                                        display: none;
                                    }

                                    #interface-container > #editor-container {
                                        display: block !important;
                                        width: 100% !important;
                                    }

                                    #interface-container {
                                        display: block !important;
                                        width: 100% !important;
                                    }
                                }
                            `}
                        />
                        <SpellCheck members={patronsDisplayNames} editorDiv='editor-container' editorsInitialized={isCleaned} allowGoToMisspelling={props.allowGoToMisspelling} toggleAllowGoToMisspelling={props.toggleAllowGoToMisspelling} makeToast={props.makeToast} selectedP={selectedP} lines={lines} />
                    </div>
                </div>
            </React.Fragment>
        }
    </>
}


export default connect(
    (state) => {
        const { committee, members } = state;
        return {
            committee,
            members
        }
    },
    (dispatch) => {
        return {
            actions: bindActionCreators(Object.assign({}, committeeActionCreators, memberActionCreators), dispatch)
        }
    }
)(WebEditor)


const formats = {
    section: {
        block: 'p', attributes: { class: 'section' }
    },
    ldtitle: {
        block: 'p', attributes: { class: 'ldtitle' }
    },
    enactstatement: {
        block: 'p', attributes: { class: 'enactstm' }
    },
    enactclause: {
        block: 'p', attributes: { class: 'enactcls' }
    },
    billnumber: {
        block: 'p', attributes: { class: 'billno' }
    },
    catchline: {
        block: 'p', attributes: { class: 'catchln' }
    },
    rejected: {
        block: 'p', attributes: { class: 'rejected' }
    },
    i10: {
        block: 'p', attributes: { class: 'i10' }
    },
    i0: {
        block: 'p', attributes: { class: 'i0' }
    },
    i15: {
        block: 'p', attributes: { class: 'i15' }
    },
    i20: {
        block: 'p', attributes: { class: 'i20' }
    },
    customcenter: {
        block: 'p', attributes: { class: 'center' }
    },
    strikethrough: {
        inline: 's'
    },
    end: {
        block: 'p', attributes: { class: 'end' }
    },
    textind: {
        block: 'p', attributes: { class: 'textind' }
    },
    textbl: {
        block: 'p', attributes: { class: 'textbl' }
    },
    textbi: {
        block: 'p', attributes: { class: 'textbi' }
    },
    textasis: {
        block: 'p', attributes: { class: 'textasis' }
    },
    table: {
        block: 'p', attributes: { class: 'table' }
    },
    testc: {
        block: 'p', attributes: { class: 'testc' }
    },
    tablec: {
        block: 'p', attributes: { class: 'tablec' }
    },
    tblhdn: {
        block: 'p', attributes: { class: 'tblhdn' }
    },
    tbllft: {
        block: 'p', attributes: { class: 'tbllft' }
    },
    tblctr: {
        block: 'p', attributes: { class: 'tblctr' }
    },
    tblrt: {
        block: 'p', attributes: { class: 'tblrt' }
    },
    txtctr: {
        block: 'p', attributes: { class: 'txtctr' }
    },
    drafter: {
        block: 'p', attributes: { class: 'drafter' }
    },
    billblank: {
        block: 'p', attributes: { class: 'billblank' }
    },
    pro: {
        block: 'p', attributes: { class: 'pro' }
    },
    sumtext: {
        block: 'p', attributes: { class: 'sumtext' }
    },
    lddata: {
        block: 'p', attributes: { class: 'lddata' }
    },
    ld: {
        block: 'p', attributes: { class: 'ld' }
    },
    patctr: {
        block: 'p', attributes: { class: 'patctr' }
    },
    patalt: {
        block: 'p', attributes: { class: 'patalt' }
    },
    textrt: {
        block: 'p', attributes: { class: 'textrt' }
    },
    testhdrchar: {
        block: 'p', attributes: { class: 'testhdrchar' }
    },
    texthdr: {
        block: 'p', attributes: { class: 'texthdr' }
    },
    new: {
        block: 'p', classes: 'new'
    }
}

const style_formats = [
    { title: 'Section', format: 'section' },
    { title: 'LD Title', format: 'ldtitle' },
    { title: 'Enact Statement', format: 'enactstatement' },
    { title: 'Enact Clause', format: 'enactclause' },
    { title: 'Bill Number', format: 'billnumber' },
    { title: 'Catchline', format: 'catchline' },
    { title: 'Rejected', format: 'rejected' },
    { title: 'i10', format: 'i10' },
    { title: 'i0', format: 'i0' },
    { title: 'i15', format: 'i15' },
    { title: 'i20', format: 'i20' },
    { title: 'Center', format: 'customcenter' },
    { title: "End", format: "end" },
    { title: "Textind", format: "textind" },
    { title: "Textbl", format: "textbl" },
    { title: "Textbi", format: "textbi" },
    { title: "Textasis", format: "textasis" },
    { title: "Table", format: "table" },
    { title: "Testc", format: "testc" },
    { title: "Tablec", format: "tablec" },
    { title: "Tblhdn", format: "tblhdn" },
    { title: "Tbllft", format: "tbllft" },
    { title: "Tblctr", format: "tblctr" },
    { title: "Tblrt", format: "tblrt" },
    { title: "Txtctr", format: "txtctr" },
    { title: "Drafter", format: "drafter" },
    { title: "Billblank", format: "billblank" },
    { title: "Pro", format: "pro" },
    { title: "Sumtext", format: "sumtext" },
    { title: "Lddata", format: "lddata" },
    { title: "Ld", format: "ld" },
    { title: "Patctr", format: "patctr" },
    { title: "Patalt", format: "patalt" },
    { title: "Textrt", format: "textrt" },
    { title: "Testhdrchar", format: "testhdrchar" },
    { title: "Texthdr", format: "texthdr" },
    { title: "New", format: "new" }
]

const usePreviousValue = value => {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
};
